import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Eye } from "lucide-react";
import { toast } from "sonner";

/**
 * Inline "View as this user" trigger. Available only when the *real* session
 * is admin. Starts impersonation, then navigates to the appropriate dashboard.
 *
 * Props:
 *  - userId (required) — target user_id
 *  - userEmail / userRole — purely for the toast / display
 *  - size / variant / className — pass-through Button props
 */
export default function ImpersonateButton({
  userId, userEmail, userRole, size = "sm", variant = "outline", className = "",
  label,
}) {
  const navigate = useNavigate();
  const { isAdminReal, startImpersonation, realUser } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!isAdminReal || !userId || userId === realUser?.user_id) return null;

  const run = async () => {
    setBusy(true);
    try {
      const r = await startImpersonation(userId, "Admin view-as");
      sessionStorage.setItem("financelocal:imp_return_to", window.location.pathname);
      toast.success(`Viewing as ${r.target?.email || userEmail || userId}`);
      if (r.target?.role === "dealer") navigate("/dealer");
      else navigate("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start impersonation");
    } finally { setBusy(false); }
  };

  return (
    <Button data-testid={`imp-as-${userId}`} size={size} variant={variant}
      className={`rounded-none uppercase text-xs tracking-wider ${className}`}
      onClick={run} disabled={busy}>
      <Eye className="w-3.5 h-3.5 mr-1.5" />
      {busy ? "Starting…" : (label || `View as ${userRole || "user"}`)}
    </Button>
  );
}
